import { useState, useEffect } from "react";
import { Star, TrendingUp, Sparkles } from "lucide-react";
import { Fireworks } from "./Fireworks";
import { getTotalXPForLevel, getPrestigeAndLevelFromXP } from "@/hooks/useXP";

// Animated XP Bar component for win screen
export function XPGainAnimation({ 
  xpGained, 
  previousXP, 
  newXP, 
  previousLevel,
  newLevel,
  neededXP,
  onAnimationComplete 
}) {
  const [displayedXP, setDisplayedXP] = useState(0);
  const [barProgress, setBarProgress] = useState(0);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);
  const [animationPhase, setAnimationPhase] = useState('idle'); // idle, counting, filling, levelup, complete

  // Get prestige info for display
  const newInfo = getPrestigeAndLevelFromXP(newXP);
  const previousInfo = getPrestigeAndLevelFromXP(previousXP);

  // Calculate progress percentages
  const previousProgress = neededXP > 0 ? (previousInfo.currentXP / neededXP) * 100 : 0;
  const newProgress = neededXP > 0 ? (newInfo.currentXP / newInfo.neededXP) * 100 : 100;
  const leveledUp = newLevel > previousLevel || newInfo.prestige > previousInfo.prestige;

  useEffect(() => {
    let isMounted = true;
    
    // Start animation sequence
    const startAnimation = async () => {
      if (!isMounted) return;
      setAnimationPhase('counting');
      
      // Phase 1: Count up XP numbers
      const duration = 1500;
      const steps = 30;
      const increment = xpGained / steps;
      
      for (let i = 1; i <= steps; i++) {
        if (!isMounted) return;
        await new Promise(resolve => setTimeout(resolve, duration / steps));
        setDisplayedXP(Math.round(increment * i));
      }
      if (!isMounted) return;
      setDisplayedXP(xpGained);
      
      // Phase 2: Animate bar
      setAnimationPhase('filling');
      await new Promise(resolve => setTimeout(resolve, 200));
      
      if (!isMounted) return;
      
      if (leveledUp) {
        // Fill to 100%, then show level up
        setBarProgress(100);
        await new Promise(resolve => setTimeout(resolve, 800));
        
        if (!isMounted) return;
        setAnimationPhase('levelup');
        setShowLevelUp(true);
        setShowFireworks(true); // 🎆 Start fireworks!
        await new Promise(resolve => setTimeout(resolve, 3000)); // Extended for fireworks
        
        if (!isMounted) return;
        // Reset bar and fill to new progress
        setBarProgress(0);
        await new Promise(resolve => setTimeout(resolve, 300));
        setBarProgress(newProgress);
        setShowFireworks(false); // Stop fireworks
      } else {
        setBarProgress(newProgress);
      }
      
      await new Promise(resolve => setTimeout(resolve, 500));
      if (!isMounted) return;
      setAnimationPhase('complete');
      onAnimationComplete?.();
    };

    const timer = setTimeout(startAnimation, 500);
    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [xpGained, previousProgress, newProgress, leveledUp, neededXP, newLevel, previousLevel]); // Removed onAnimationComplete from deps

  return (
    <div className="w-full space-y-4">
      {/* 🎆 Fireworks overlay for level up */}
      <Fireworks active={showFireworks} duration={3000} intensity="high" />
      
      {/* XP Gained Display */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Star className="w-5 h-5 text-warning animate-pulse" />
          <span className="font-body text-muted-foreground text-sm">XP Earned</span>
          <Star className="w-5 h-5 text-warning animate-pulse" />
        </div>
        <div className="font-display text-4xl font-bold text-warning">
          +{displayedXP.toLocaleString()}
          <span className="text-lg ml-1">XP</span>
        </div>
      </div>

      {/* XP Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between items-center text-sm font-body">
          <span className="text-muted-foreground">
            {newInfo.prestige > 0 && <span className="text-purple-500 font-bold">P{newInfo.prestige} </span>}
            Level {showLevelUp ? newLevel : previousLevel}
          </span>
          <span className="text-muted-foreground">
            {newInfo.prestige > 0 && <span className="text-purple-500 font-bold">P{newInfo.prestige} </span>}
            {newInfo.level >= 50 && newInfo.prestige < 5 ? (
              <span>Prestige {newInfo.prestige + 1}</span>
            ) : (
              <span>Level {showLevelUp ? newLevel + 1 : previousLevel + 1}</span>
            )}
          </span>
        </div>
        
        <div className={`relative h-6 bg-muted rounded-full overflow-hidden border-2 border-warning/30 ${showLevelUp ? 'animate-glow-pulse' : ''}`}>
          {/* Background glow effect */}
          <div 
            className="absolute inset-0 bg-gradient-to-r from-warning/20 via-yellow-400/20 to-warning/20 animate-pulse"
            style={{ opacity: animationPhase === 'filling' ? 1 : 0.3 }}
          />
          
          {/* Progress bar */}
          <div 
            className="h-full bg-gradient-to-r from-warning via-yellow-400 to-amber-500 rounded-full transition-all duration-700 ease-out relative"
            style={{ width: `${barProgress}%` }}
          >
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
          </div>

          {/* XP text inside bar */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-display font-bold text-xs text-foreground drop-shadow-md">
              {Math.round(barProgress)}%
            </span>
          </div>
        </div>

        {/* XP Numbers */}
        <div className="flex justify-between items-center text-xs font-body text-muted-foreground">
          <span>Total: {newXP.toLocaleString()} XP</span>
          <span>
            {newInfo.isWaitingForPrestige 
              ? `${(100000 - newInfo.currentXP).toLocaleString()} XP to Prestige ${newInfo.prestige + 1}`
              : `${(newInfo.neededXP - newInfo.currentXP).toLocaleString()} XP to next level`
            }
          </span>
        </div>
      </div>

      {/* Level Up Celebration */}
      {showLevelUp && (
        <div className="animate-bounce-in text-center py-4">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-accent via-primary to-accent rounded-full shadow-lg border-2 border-white/30 animate-glow-pulse">
            <Sparkles className="w-6 h-6 text-white animate-spin" />
            <div>
              <p className="font-display font-bold text-white text-xl">🎉 LEVEL UP! 🎉</p>
              <p className="font-body text-white/80 text-sm">
                Level {previousLevel} → Level {newLevel}
              </p>
            </div>
            <Sparkles className="w-6 h-6 text-white animate-spin" />
          </div>
        </div>
      )}
    </div>
  );
}

// Compact XP display for header/profile
export function XPBadge({ level, currentXP, neededXP, totalXP, prestige = 0, showDetails = false, compact = false }) {
  const progress = neededXP > 0 ? (currentXP / neededXP) * 100 : 100;

  return (
    <div className="flex flex-col items-center gap-1">
      {/* Level Badge */}
      <div className="relative">
        <div className={`rounded-full flex items-center justify-center shadow-md border border-white/50 ${
          compact ? 'w-8 h-8' : 'w-12 h-12 border-2'
        } ${
          prestige > 0 
            ? 'bg-gradient-to-br from-purple-500 via-pink-500 to-purple-600' 
            : 'bg-gradient-to-br from-warning via-yellow-400 to-amber-500'
        }`}>
          <span className={`font-display font-bold text-white ${compact ? 'text-xs' : 'text-lg'}`}>{level}</span>
        </div>
        {prestige > 0 && (
          <div className={`absolute -top-1 -right-1 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center border border-white ${
            compact ? 'w-3.5 h-3.5' : 'w-5 h-5'
          }`}>
            <span className={`font-display font-bold text-white ${compact ? 'text-[8px]' : 'text-[10px]'}`}>{prestige}</span>
          </div>
        )}
        {!compact && <TrendingUp className="absolute -bottom-1 -right-1 w-4 h-4 text-warning bg-card rounded-full p-0.5" />}
      </div>

      {showDetails && (
        <div className="flex flex-col items-center min-w-[100px]">
          <span className="text-xs font-body text-muted-foreground flex items-center gap-1">
            {prestige > 0 && (() => {
              const emblems = {
                1: { symbol: 'I', color: 'text-emerald-500' },
                2: { symbol: 'II', color: 'text-sky-500' },
                3: { symbol: 'III', color: 'text-violet-500' },
                4: { symbol: 'IV', color: 'text-rose-500' },
                5: { symbol: 'V', color: 'text-cyan-400' }
              };
              const e = prestige >= 6 ? { symbol: 'M', color: 'text-amber-500' } : emblems[prestige];
              return e ? <span className={`font-bold ${e.color}`}>{e.symbol}</span> : null;
            })()}
            Level {level}
          </span>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden mt-1">
            <div 
              className={`h-full rounded-full transition-all duration-300 ${
                prestige > 0 
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
                  : 'bg-gradient-to-r from-warning to-amber-500'
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-xs text-warning font-semibold mt-0.5">
            {currentXP.toLocaleString()}/{neededXP.toLocaleString()} XP
          </span>
        </div>
      )}
    </div>
  );
}

// Mini XP display for compact spaces
export function XPMini({ level, prestige = 0 }) {
  // Roman numeral emblems
  const getEmblem = (p) => {
    if (p >= 6) return { symbol: 'M', color: 'text-amber-500', bg: 'bg-amber-500/20' };
    const emblems = {
      1: { symbol: 'I', color: 'text-emerald-500', bg: 'bg-emerald-500/20' },
      2: { symbol: 'II', color: 'text-sky-500', bg: 'bg-sky-500/20' },
      3: { symbol: 'III', color: 'text-violet-500', bg: 'bg-violet-500/20' },
      4: { symbol: 'IV', color: 'text-rose-500', bg: 'bg-rose-500/20' },
      5: { symbol: 'V', color: 'text-cyan-400', bg: 'bg-cyan-500/20' }
    };
    return emblems[p] || null;
  };
  
  const emblem = prestige > 0 ? getEmblem(prestige) : null;
  
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border ${
      emblem 
        ? `${emblem.bg} border-current/30` 
        : 'bg-warning/20 border-warning/30'
    }`}>
      <Star className={`w-3 h-3 ${emblem ? `${emblem.color} fill-current` : 'text-warning fill-warning'}`} />
      <span className={`font-display font-bold text-xs ${emblem ? emblem.color : 'text-warning'}`}>
        {emblem && <span className="mr-0.5">{emblem.symbol}</span>}Lv.{level}
      </span>
    </div>
  );
}
